import * as XLSX from 'xlsx';
import { formatNumbers } from './formatNumbers.js';
import IconCalendar from '../components/images/IconCalendar.jsx';
import IconContact from '../components/images/IconContact.jsx';
import IconBulb from '../components/images/IconBulb.jsx';
import IconTicket from '../components/images/IconTicket.jsx';
import IconNews from '../components/images/IconNews.jsx';
import React from 'react';

export const convertToUrlSearchParams = (params) => {
    const filteredParams = {};

    Object.keys(params).forEach((key) => {
        const value = params[key];
        if (value) {
            filteredParams[key] = String(value);
        }
    });

    return new URLSearchParams(filteredParams);
};

export const convertDateAndTimeToUTCDateTime = (date, datetime = null) => {
    const newDate = new Date(date);
    const newDateTime = datetime ? new Date(datetime) : null;

    const dateUtc = Date.UTC(
        newDate.getFullYear(),
        newDate.getMonth(),
        newDate.getDate(),
        newDateTime ? newDateTime.getUTCHours() : 0,
        newDateTime ? newDateTime.getUTCMinutes() : 0,
        newDateTime ? newDateTime.getUTCSeconds() : 0
    );

    return new Date(dateUtc);
};

export const fromUtcDateTime = (dateTime) => {
    const newDate = new Date(dateTime);

    const dateUtc = Date.UTC(
        newDate.getFullYear(),
        newDate.getMonth(),
        newDate.getDate(),
        newDate.getUTCHours(),
        newDate.getUTCMinutes(),
        newDate.getUTCSeconds()
    );

    return new Date(dateUtc);
};

export const findItem = (item, id) => {
    if (item.data === id) {
        return item.key;
    }

    if (item.children && item.children.length > 0) {
        for (let i = 0; i < item.children.length; i++) {
            const result = findItem(item.children[i], id);
            if (result !== undefined) {
                return result;
            }
        }
    }

    return undefined;
};

export const collectChildIds = (parent) => {
    let collectedIds = [parent.data];

    if (parent.children && parent.children.length > 0) {
        parent.children.forEach((child) => {
            collectedIds = collectedIds.concat(collectChildIds(child));
        });
    }

    return collectedIds;
};

export const collectParentIds = (tree, id, parents = []) => {
    for (const node of tree) {
        if (node.data === id) {
            return parents;
        }

        if (node.children && node.children.length > 0) {
            const foundInChildren = collectParentIds(node.children, id, [...parents, node.data]);
            if (foundInChildren.length > 0) {
                return foundInChildren;
            }
        }
    }

    return [];
};

export const findParentNode = (node, id) => {
    if (node.data === id) {
        return node;
    }
    if (node.children) {
        for (let child of node.children) {
            const found = findParentNode(child, id);
            if (found) return found;
        }
    }
    return null;
};

export const removeChildrenRecursively = (updatedSelection, parentKey) => {
    Object.keys(updatedSelection).forEach((key) => {
        if (String(key).startsWith(parentKey + '-')) {
            delete updatedSelection[key];
            removeChildrenRecursively(updatedSelection, key);
        }
    });
};

export const removeEmptyParents = (updatedSelection, key) => {
    if (!key || typeof key !== 'string') return;

    const parentKey = key.split('-').slice(0, -1).join('-');

    if (parentKey && updatedSelection[parentKey]) {
        const parentChildren = Object.keys(updatedSelection).filter((childKey) =>
            String(childKey).startsWith(parentKey + '-')
        );

        if (parentChildren.length === 0) {
            delete updatedSelection[parentKey];
            removeEmptyParents(updatedSelection, parentKey);
        }
    }
};

export const removeFromForm = (idsToRemove, property, formFields, removeFunc) => {
    const indexesToRemove = [];

    formFields.forEach((item, idx) => {
        if (idsToRemove.includes(item[property])) {
            indexesToRemove.push(idx);
        }
    });

    indexesToRemove.sort((a, b) => b - a).forEach((idx) => removeFunc(idx));
};

export const addTreeSelection = (tree, fieldName, value, property, append, remove, fields, setSelected) => {
    setSelected(value);

    Object.entries(value).forEach(([key, type]) => {
        let split = key.split('-');
        let element = tree[split[0]];

        if (split.length > 1) {
            split.slice(1).forEach((index) => {
                element = element.children[index];
            });
        }

        if (type.checked && !element.noAppend) {
            append({ name: element.label, [property]: element.data });
        }
    });

    const indicesToRemove = [];
    fields.forEach((item, index) => {
        if (!value[item.id]) {
            indicesToRemove.push(index);
        }
    });

    indicesToRemove.sort((a, b) => b - a).forEach((index) => remove(index));
};

export const removeAttribute = (tree, index, id, func, property, fields, remove) => {
    let removedKey = '';

    for (let i = 0; i < tree.length; i++) {
        removedKey = findItem(tree[i], id);
        if (removedKey !== undefined) break;
    }

    if (removedKey !== '') {
        func((prev) => {
            const updatedSelection = { ...prev };

            removeChildrenRecursively(updatedSelection, removedKey);

            if (updatedSelection[removedKey]) {
                delete updatedSelection[removedKey];
            }

            removeEmptyParents(updatedSelection, removedKey);

            return Object.keys(updatedSelection).length ? updatedSelection : null;
        });

        let parentNode = null;
        for (let i = 0; i < tree.length; i++) {
            parentNode = findParentNode(tree[i], id);
            if (parentNode) break;
        }

        if (parentNode) {
            const idsToRemove = collectChildIds(parentNode).concat(collectParentIds(tree, parentNode.data, []));
            removeFromForm(idsToRemove, property, fields, remove);
        }
    }
};

export const exportToExcel = (data, filename = 'data.xlsx') => {
    const workSheet = XLSX.utils.json_to_sheet(data);
    workSheet['!cols'] = Array(Object.keys(data[0] || {}).length).fill({ wch: 40 });

    Object.keys(workSheet).forEach((cellAddress) => {
        if (!cellAddress.startsWith('!')) {
            const cell = workSheet[cellAddress];

            if (typeof cell.v === 'string' && cell.v.includes(',')) {
                cell.v = cell.v.replace(/,\s*/g, '\n');
            }

            if (!cell.s) cell.s = {};
            if (!cell.s.alignment) cell.s.alignment = {};
            cell.s.alignment.wrapText = true;
        }
    });
    const workBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workBook, workSheet, 'Sheet1');

    const workBookOut = XLSX.write(workBook, { bookType: 'xlsx', type: 'array', cellStyles: true });
    const blob = new Blob([workBookOut], { type: 'application/octet-stream' });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', filename);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export const getSortedValuesByYear = (data) => {
    const yearMap = data.reduce((map, item) => {
        const year = item.fiscalDateEnding.slice(0, 4);
        if (!map[year] || item.fiscalDateEnding > map[year].fiscalDateEnding) {
            map[year] = item;
        }
        return map;
    }, {});

    const arrayOfYears = Object.values(yearMap);

    arrayOfYears.sort(
        (a, b) => parseInt(a.fiscalDateEnding.substring(0, 4), 10) - parseInt(b.fiscalDateEnding.substring(0, 4), 10)
    );
    return arrayOfYears;
};

export const formatCurrencyOrDash = (value) => {
    if (value === null || value === 'None' || value === undefined || isNaN(Number(value))) {
        return '-';
    }
    return formatNumbers(Number(value), '$');
};

export const renderIcon = (type, color = null) => {
    const className = `${color ?? 'bg-theme1-gold'} rounded-full stroke-theme1-white p-[5px] w-[33px] h-[33px] mr-2`;

    const icons = {
        meeting: IconCalendar,
        contact: IconContact,
        'contact-duplicates': IconContact,
        lead: IconBulb,
        ticket: IconTicket,
        'contact-news': IconNews,
        'organization-news': IconNews
    };

    const icon = icons[type];
    return icon ? React.createElement(icon, { className }) : null;
};

export const link = (notification) => {
    const urlMap = {
        ticket: '/my-tickets/',
        'organization-news': '/organizations/',
        'contact-news': '/contacts/view/',
        lead: '/leads/',
        'contact-duplicates': '/contacts/duplicates',
        meeting: '/calendar/view/'
    };
    const base = urlMap[notification.type] || '/';
    if (notification.type === 'contact-duplicates') {
        return `${base}?scrollTo=${notification.type_id}`;
    }
    return `${base}${notification.type_id}`;
};
